import type { MoodleCourseSection } from '@/types/moodle.types';

export function cn(
  ...inputs: (string | undefined | null | boolean | { [key: string]: boolean })[]
) {
  return inputs
    .filter(Boolean)
    .map((input) => {
      if (typeof input === 'object') {
        return Object.entries(input!)
          .filter(([_, value]) => value)
          .map(([key]) => key)
          .join(' ');
      }
      return input;
    })
    .join(' ');
}

/**
 * Trims extra whitespace, removes empty HTML tags (e.g. <p></p>, <p>&nbsp;</p>),
 * and eliminates leading/trailing spacer/break structures to clean up Moodle's output.
 */
export function cleanHtml(html: string | undefined | null): string {
  if (!html) return '';

  let cleaned = html
    // Remove empty paragraphs, divs, headings, spans (with or without non-breaking spaces or whitespace)
    .replace(/<(p|div|span|h[1-6]|span)[^>]*>\s*(?:&nbsp;|\s)*<\/\1>/gi, '')
    // Remove standalone br tags or multiple consecutive br tags at start/end
    .replace(/^(?:\s*<br\s*\/?>\s*)+/gi, '')
    .replace(/(?:\s*<br\s*\/?>\s*)+$/gi, '')
    // Clean spaces around open/close tags
    .trim();

  return cleaned;
}

export function customCourseSectionFilter(
  sections: MoodleCourseSection[],
  courseId: number,
  moduleLockStatus: Map<number, boolean>,
  sectionLockStatus: Map<number, boolean>
): MoodleCourseSection[] {
  if (courseId !== 5 || !sections) return sections || [];

  return sections.reduce<MoodleCourseSection[]>((acc, section) => {
    if (section.name === 'Your Clarity Results') {
      // 1. Filter out locked modules
      const unlockedModules = (section.modules || []).filter(
        (m) => moduleLockStatus.get(m.id) !== true
      );

      // 2. Check if we should hide the entire section
      // Condition: all modules in this section are locked
      if (unlockedModules.length === 0) {
        // Find previous section that actually has modules and is visible
        const prevSection = acc[acc.length - 1];
        if (prevSection) {
          const prevModules = prevSection.modules || [];
          const allPrevUnlocked = prevModules.every((m) => moduleLockStatus.get(m.id) !== true);
          if (allPrevUnlocked && prevModules.length > 0) {
            // Hide the entire section by not adding it to acc
            return acc;
          }
        }
      }

      // Add the section with filtered modules
      acc.push({
        ...section,
        modules: unlockedModules,
      });
    } else {
      acc.push(section);
    }
    return acc;
  }, []);
}

export function getModuleDisplayName(module: { name: string; displayName?: string }): string {
  if (module.displayName && !module.displayName.startsWith('TOOL:')) {
    return module.displayName;
  }
  if (module.name && module.name.startsWith('TOOL:')) {
    const withoutPrefix = module.name.slice(5); // 'TOOL:'.length
    const colonIdx = withoutPrefix.indexOf(':');
    if (colonIdx !== -1) {
      const displayName = withoutPrefix.slice(colonIdx + 1).trim();
      if (displayName) return displayName;
    }
  }
  return module.displayName || module.name || '';
}
